// src/components/HRComponents/CaseDetailModal.js
import React, { useState, useMemo, useRef } from "react";
import { X, Send, Download, Upload, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from "../../customStyles/SelectDropdown";
import { usePermission } from "../../hooks/usePermission";

export default function CaseDetailModal({ selectedCase, setSelectedCase, user, employees, onUpdate }) {
  const [commentText, setCommentText] = useState("");
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [uploading, setUploading] = useState(false); // ஆவணப் பதிவேற்ற லோடிங் நிலை
  
  const fileInputRef = useRef(null);
  const { hasPermission } = usePermission();

  // 🔐 எடிட் மற்றும் அசைன் செய்யும் அதிகாரம் படைத்தவர்கள் மட்டுமே (Owner, Admin, HR with edit permission)
  const canEditAndAssign = useMemo(() => {
    return (
      user?.role === "owner" || 
      user?.role === "hr" || 
      user?.role === "admin" || 
      hasPermission("hr:case-tracker", "edit")
    );
  }, [user, hasPermission]);

  // கமெண்ட் பதிவிடும் செயல்பாடு
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      const url = SummaryApi.addComment?.url ? SummaryApi.addComment.url(selectedCase._id) : `/api/cases/${selectedCase._id}/comments`;
      const res = await api.post(url, { message: commentText, isInternal: isInternalComment });
      if (res.data.success) {
        toast.success("Comment added successfully.");
        setCommentText("");
        setIsInternalComment(false);
        setSelectedCase(res.data.data);
        onUpdate();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add comment.");
    }
  };

  // நிலை மாற்றம் (Status Transition) செயல்பாடு
  const handleUpdateStatus = async (status, note = "") => {
    try {
      const baseUrl = SummaryApi.updateCaseStatus?.url || "/api/cases";
      const url = `${baseUrl}/${selectedCase._id}/status`;
      const res = await api.patch(url, { status, note });
      if (res.data.success) {
        toast.success(`Case status updated to ${status}.`);
        setSelectedCase(res.data.data);
        onUpdate();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Status transition failed.");
    }
  };

  // பொறுப்பாளர் நியமிப்பு (Reassignment) செயல்பாடு
  const handleReassign = async (assigneeId) => {
    try {
      const baseUrl = SummaryApi.reassignCase?.url || "/api/cases";
      const url = `${baseUrl}/${selectedCase._id}/reassign`;
      const res = await api.patch(url, { assignedTo: assigneeId });
      if (res.data.success) {
        toast.success("Case reassigned successfully.");
        setSelectedCase(res.data.data);
        onUpdate();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to assign representative.");
    }
  };

  // ஆவணங்கள் / கோப்புகள் பதிவேற்றம் (Document Upload) செயல்பாடு
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const baseUrl = "/api/cases";
      const url = `${baseUrl}/${selectedCase._id}/attachments`;
      const res = await api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data.success) {
        toast.success("Document uploaded successfully.");
        setSelectedCase(res.data.data);
        onUpdate();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload document.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 top-[57px] md:top-[65px] z-[65] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-5.5xl rounded-2xl shadow-2xl border flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="p-4 sm:p-5 border-b flex justify-between items-center shrink-0 bg-slate-50">
          <div className="flex gap-3 items-center">
            <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center border font-bold text-sm">
              {selectedCase.caseId?.substring(0,3) || "CAS"}
            </div>
            <div className="text-left">
              <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight">{selectedCase.title}</h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">ID: {selectedCase.caseId} • {selectedCase.categoryId?.name}</p>
            </div>
          </div>
          <button onClick={() => setSelectedCase(null)} className="p-1 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg border">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* ── LEFT COLUMN: DETAILS & COMMENTS (ALL USERS CAN VIEW) ── */}
            <div className="lg:col-span-2 space-y-5">
              
              {/* Description */}
              <div className="bg-slate-50 border rounded-xl p-4 text-xs text-left">
                <span className="block text-[10px] text-gray-400 font-bold uppercase mb-2">Description</span>
                <p className="text-slate-700 whitespace-pre-wrap italic leading-relaxed">"{selectedCase.description}"</p>
              </div>

              {/* Timeline Comments */}
              <div className="bg-white border rounded-xl p-4 shadow-sm space-y-4">
                <span className="block text-[10px] text-gray-400 font-bold uppercase text-left">Timeline & Comments</span>
                <div className="space-y-3 max-h-[220px] overflow-y-auto">
                  {selectedCase.comments?.map((comment, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border text-left text-xs ${comment.isInternal ? "bg-amber-50/40 border-amber-100" : "bg-slate-50/60 border-slate-100"}`}>
                      <div className="flex justify-between items-center mb-1 text-[10px] text-gray-400">
                        <span className="font-bold text-slate-700">{comment.addedBy?.firstName} {comment.addedBy?.lastName}</span>
                        <span>{new Date(comment.createdAt).toLocaleString()}</span>
                      </div>
                      <p className="text-slate-600">"{comment.message}"</p>
                    </div>
                  ))}
                  {(!selectedCase.comments || selectedCase.comments.length === 0) && (
                    <p className="text-xs text-slate-400 italic text-left">No comment history available.</p>
                  )}
                </div>

                <form onSubmit={handleAddComment} className="pt-2 border-t flex flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <input type="text" required value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Type communication update..." className="flex-1 border rounded-lg px-3 py-2 text-xs outline-none focus:border-red-400 h-9" />
                    <button type="submit" className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"><Send className="w-4 h-4" /></button>
                  </div>
                </form>
              </div>
            </div>

            {/* ── RIGHT COLUMN: CONTROLS & DOCS ── */}
            <div className="space-y-5 text-xs text-left">
              
              {/* 1. Technical Specs - Read Only (All Users View) */}
              <div className="bg-white border rounded-xl p-4 shadow-sm space-y-3.5">
                <span className="block text-[10px] text-gray-400 font-bold uppercase border-b pb-1">Technical Specs</span>
                <div className="space-y-2">
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">ASSIGNED REPRESENTATIVE</span>
                    <span className="font-bold text-slate-800">
                      {selectedCase.assignedTo ? `${selectedCase.assignedTo.firstName} ${selectedCase.assignedTo.lastName}` : "Unassigned"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">CURRENT STATUS</span>
                    <span className="font-bold text-slate-800 uppercase">{selectedCase.status?.replace("_", " ")}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block font-bold">DUE DATE</span>
                    <span className="font-bold text-slate-800">{selectedCase.dueDate ? new Date(selectedCase.dueDate).toLocaleDateString() : "No deadline"}</span>
                  </div>
                </div>
              </div>

              {/* 2. ADMIN ACTION PANEL - Visible ONLY to Owner, Admin & HR with edit access */}
              {canEditAndAssign && (
                <div className="bg-red-50/40 border border-red-100 rounded-xl p-4 space-y-4">
                  <span className="block text-[10px] text-red-700 font-bold uppercase border-b border-red-100 pb-1">Admin Control Panel</span>
                  
                  {/* Reassign selection dropdown */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] text-slate-500 block font-bold">REASSIGN REPRESENTATIVE</span>
                    <SelectDropdown 
                      options={employees} 
                      value={selectedCase.assignedTo?._id || selectedCase.assignedTo || ""} 
                      onChange={handleReassign} 
                      placeholder="Choose Assignee" 
                      valueKey="value" 
                      labelKey="label" 
                      ChildClassName="h-8 text-xs border-slate-200" 
                      searchable={true} 
                      zIndex={70}
                    />
                  </div>

                  {/* Status Transitions Controls */}
                  {selectedCase.status !== "closed" && (
                    <div className="space-y-2 pt-1">
                      <span className="text-[10px] text-slate-500 block font-bold">TRANSITION STATE</span>
                      <div className="grid grid-cols-1 gap-2">
                        {selectedCase.status === "pending" && (
                          <>
                            <button onClick={() => handleUpdateStatus("in_review")} className="py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-colors">Move to Review</button>
                            <button onClick={() => handleUpdateStatus("rejected", "Rejected.")} className="py-1.5 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded-lg transition-colors">Reject</button>
                          </>
                        )}
                        {selectedCase.status === "in_review" && (
                          <>
                            <button onClick={() => handleUpdateStatus("approved", "Approved.")} className="py-1.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors">Approve</button>
                            <button onClick={() => handleUpdateStatus("rejected", "Rejected on review.")} className="py-1.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors">Reject</button>
                          </>
                        )}
                        {["approved", "rejected"].includes(selectedCase.status) && (
                          <button onClick={() => handleUpdateStatus("closed", "Closed and resolved.")} className="py-1.5 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-lg transition-colors">Close & Lock</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 3. Attachments & Interactive Document Upload Section */}
              <div className="bg-white border rounded-xl p-4 shadow-sm space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="block text-[10px] text-gray-400 font-bold uppercase">Attachments</span>
                  
                  {/* File Upload Trigger */}
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-1 text-[10px] font-bold text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    {uploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    Upload Doc
                  </button>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleFileUpload} 
                    className="hidden" 
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                  />
                </div>

                {/* Attachments List */}
                <div className="space-y-2">
                  {selectedCase.attachments?.length > 0 ? (
                    selectedCase.attachments.map((file, idx) => (
                      <a key={idx} href={file.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-2 border border-slate-100 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                        <span className="truncate font-medium text-slate-700 max-w-[150px]">{file.fileName || "doc_file"}</span>
                        <Download className="w-3.5 h-3.5 text-red-600 shrink-0" />
                      </a>
                    ))
                  ) : (
                    <p className="text-slate-400 italic text-center py-2">No files linked to this case.</p>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-end gap-3 shrink-0">
          <button onClick={() => setSelectedCase(null)} className="px-4 py-2 bg-white text-slate-700 border rounded-lg text-xs font-bold uppercase hover:bg-slate-50 transition-colors">Close</button>
        </div>
      </div>
    </div>
  );
}